import { Expiration, SupportedLanguage } from './types';
import { czech, latvian, lithuanian, polish, slavic, slovenian } from './pluralization';

// eslint-disable-next-line no-unused-vars
type ExpireFormat = (n: number) => string;

export type ExpireLocale = {
  session: string;
  year: ExpireFormat;
  month: ExpireFormat;
  day: ExpireFormat;
  hour: ExpireFormat;
};

const simple =
  (singular: string, plural: string): ExpireFormat =>
  (n) =>
    `${n} ${n !== 1 ? plural : singular}`;

const invariant =
  (word: string): ExpireFormat =>
  (n) =>
    `${n} ${word}`;

const withCzech =
  (s1: string, s2: string, s3: string): ExpireFormat =>
  (n) =>
    czech(n, s1, s2, s3);

const withPolish =
  (s1: string, s2: string, s3: string): ExpireFormat =>
  (n) =>
    polish(n, s1, s2, s3);

const withSlavic =
  (s1: string, s2: string, s3: string): ExpireFormat =>
  (n) =>
    slavic(n, s1, s2, s3);

const withSlovenian =
  (s1: string, s2: string, s3: string, s4: string): ExpireFormat =>
  (n) =>
    slovenian(n, s1, s2, s3, s4);

const withLatvian =
  (s1: string, s2: string): ExpireFormat =>
  (n) =>
    latvian(n, s1, s2);

const withLithuanian =
  (s1: string, s2: string, s3: string): ExpireFormat =>
  (n) =>
    lithuanian(n, s1, s2, s3);

const expireStrings: Record<SupportedLanguage, ExpireLocale> = {
  en: {
    session: 'Until end of session',
    year: simple('year', 'years'),
    month: simple('month', 'months'),
    day: simple('day', 'days'),
    hour: simple('hour', 'hours'),
  },
  de: {
    session: 'Bis zum Ende der Sitzung',
    year: simple('Jahr', 'Jahre'),
    month: simple('Monat', 'Monate'),
    day: simple('Tag', 'Tage'),
    hour: simple('Stunde', 'Stunden'),
  },
  es: {
    session: 'Hasta el final de la sesión',
    year: simple('año', 'años'),
    month: simple('mes', 'meses'),
    day: simple('día', 'días'),
    hour: simple('hora', 'horas'),
  },
  fr: {
    session: "Jusqu'à la fin de la session",
    year: simple('an', 'ans'),
    month: invariant('mois'),
    day: simple('jour', 'jours'),
    hour: simple('heure', 'heures'),
  },
  pt: {
    session: 'Até ao fim da sessão',
    year: simple('ano', 'anos'),
    month: simple('mês', 'meses'),
    day: simple('dia', 'dias'),
    hour: simple('hora', 'horas'),
  },
  ro: {
    session: 'Până la sfârșitul sesiunii',
    year: simple('an', 'ani'),
    month: simple('lună', 'luni'),
    day: simple('zi', 'zile'),
    hour: simple('oră', 'ore'),
  },
  hu: {
    session: 'Munkamenet végéig',
    year: invariant('év'),
    month: invariant('hónap'),
    day: invariant('nap'),
    hour: invariant('óra'),
  },
  et: {
    session: 'Kuni seansi lõpuni',
    year: simple('aasta', 'aastat'),
    month: simple('kuu', 'kuud'),
    day: simple('päev', 'päeva'),
    hour: simple('tund', 'tundi'),
  },
  cs: {
    session: 'Do konce relace',
    year: withCzech('rok', 'roky', 'let'),
    month: withCzech('měsíc', 'měsíce', 'měsíců'),
    day: withCzech('den', 'dny', 'dní'),
    hour: withCzech('hodina', 'hodiny', 'hodin'),
  },
  sk: {
    session: 'Do konca relácie',
    year: withCzech('rok', 'roky', 'rokov'),
    month: withCzech('mesiac', 'mesiace', 'mesiacov'),
    day: withCzech('deň', 'dni', 'dní'),
    hour: withCzech('hodina', 'hodiny', 'hodín'),
  },
  pl: {
    session: 'Do końca sesji',
    year: withPolish('rok', 'lata', 'lat'),
    month: withPolish('miesiąc', 'miesiące', 'miesięcy'),
    day: withPolish('dzień', 'dni', 'dni'),
    hour: withPolish('godzina', 'godziny', 'godzin'),
  },
  ru: {
    session: 'До конца сессии',
    year: withSlavic('год', 'года', 'лет'),
    month: withSlavic('месяц', 'месяца', 'месяцев'),
    day: withSlavic('день', 'дня', 'дней'),
    hour: withSlavic('час', 'часа', 'часов'),
  },
  uk: {
    session: 'До кінця сесії',
    year: withSlavic('рік', 'роки', 'років'),
    month: withSlavic('місяць', 'місяці', 'місяців'),
    day: withSlavic('день', 'дні', 'днів'),
    hour: withSlavic('година', 'години', 'годин'),
  },
  sr: {
    session: 'Do kraja sesije',
    year: withSlavic('godina', 'godine', 'godina'),
    month: withSlavic('mesec', 'meseca', 'meseci'),
    day: withSlavic('dan', 'dana', 'dana'),
    hour: withSlavic('sat', 'sata', 'sati'),
  },
  hr: {
    session: 'Do kraja sesije',
    year: withSlavic('godina', 'godine', 'godina'),
    month: withSlavic('mjesec', 'mjeseca', 'mjeseci'),
    day: withSlavic('dan', 'dana', 'dana'),
    hour: withSlavic('sat', 'sata', 'sati'),
  },
  bs: {
    session: 'Do kraja sesije',
    year: withSlavic('godina', 'godine', 'godina'),
    month: withSlavic('mjesec', 'mjeseca', 'mjeseci'),
    day: withSlavic('dan', 'dana', 'dana'),
    hour: withSlavic('sat', 'sata', 'sati'),
  },
  sl: {
    session: 'Do konca seje',
    year: withSlovenian('leto', 'leti', 'leta', 'let'),
    month: withSlovenian('mesec', 'meseca', 'mesece', 'mesecev'),
    day: withSlovenian('dan', 'dni', 'dni', 'dni'),
    hour: withSlovenian('ura', 'uri', 'ure', 'ur'),
  },
  mk: {
    session: 'До крајот на сесијата',
    year: simple('година', 'години'),
    month: simple('месец', 'месеци'),
    day: simple('ден', 'дена'),
    hour: simple('час', 'часа'),
  },
  lv: {
    session: 'Līdz sesijas beigām',
    year: withLatvian('gads', 'gadi'),
    month: withLatvian('mēnesis', 'mēneši'),
    day: withLatvian('diena', 'dienas'),
    hour: withLatvian('stunda', 'stundas'),
  },
  lt: {
    session: 'Iki sesijos pabaigos',
    year: withLithuanian('metai', 'metai', 'metų'),
    month: withLithuanian('mėnuo', 'mėnesiai', 'mėnesių'),
    day: withLithuanian('diena', 'dienos', 'dienų'),
    hour: withLithuanian('valanda', 'valandos', 'valandų'),
  },
};

export const expireToString = (expire: Expiration, language: SupportedLanguage): string => {
  const t = expireStrings[language] ?? expireStrings.en;
  if (expire === 'Session') {
    return t.session;
  }
  if ('years' in expire) {
    return t.year(expire.years);
  }
  if ('months' in expire) {
    return t.month(expire.months);
  }
  if ('days' in expire) {
    return t.day(expire.days);
  }

  return t.hour(expire.hours);
};
